// NetworkView — radial cluster ("spider") layout.
//
// The root sits at the centre of the canvas; each subtree is allocated
// an angular wedge proportional to its leaf count, and each child is
// placed at the midpoint of its wedge at a fixed radius from its
// parent. Children recurse with a slightly smaller radius so deep
// branches don't shoot off the canvas.
//
// Bubble size scales with subtree leaf count, so visually-heavy nodes
// (managers of big teams) read as larger. Lines render parent → child
// edges. Closest approximation we can do to "network graph" without
// dragging d3-force into the bundle.
import React, { useMemo, useState } from "react";
import { childrenOf, colorFor, initialOf, nameOf, nodeMatches, withSyntheticRoot } from "./shared";

const W       = 880;
const H       = 560;
const ROOT_R  = 26;
const LEAF_R  = 9;
const MAX_R   = 22;     // cap for big-subtree bubbles
const RADIUS0 = 130;    // distance root → depth-1 children
const SHRINK  = 0.62;   // each deeper level uses SHRINK × parent's radius
const TAU     = Math.PI * 2;

function leafCount(n) {
  const kids = childrenOf(n);
  if (kids.length === 0) return 1;
  return kids.reduce((acc, c) => acc + leafCount(c), 0);
}

function bubbleRadius(n) {
  const kids = childrenOf(n);
  if (kids.length === 0) return LEAF_R;
  // Log scaling so the difference between teams of 8 and 80 doesn't
  // produce a monster bubble next to a pea.
  const w = Math.log2(leafCount(n) + 1) * 4;
  return Math.min(MAX_R, Math.max(LEAF_R + 1, w));
}

function layoutNetwork(node, cx, cy, a0, a1, radius, depth) {
  node._pt = { x: cx, y: cy, depth };
  const kids = childrenOf(node);
  if (kids.length === 0) return;
  const totalLeaves = kids.reduce((s, c) => s + leafCount(c), 0) || 1;
  let cursor = a0;
  kids.forEach((c) => {
    const span  = ((a1 - a0) * leafCount(c)) / totalLeaves;
    const angle = cursor + span / 2;
    const childX = cx + radius * Math.cos(angle);
    const childY = cy + radius * Math.sin(angle);
    layoutNetwork(c, childX, childY, cursor, cursor + span, radius * SHRINK, depth + 1);
    cursor += span;
  });
}

function flattenWithEdges(node, nodes, edges) {
  nodes.push(node);
  childrenOf(node).forEach((c) => {
    edges.push([node, c]);
    flattenWithEdges(c, nodes, edges);
  });
}

export default function NetworkView({ tree, search, onSelectUser }) {
  const [hover, setHover] = useState(null);

  const { nodes, edges, matchKey } = useMemo(() => {
    const root = withSyntheticRoot(tree);
    layoutNetwork(root, W / 2, H / 2, -Math.PI / 2, -Math.PI / 2 + TAU, RADIUS0, 0);

    const ns = [];
    const es = [];
    flattenWithEdges(root, ns, es);

    const matches = new Set();
    if (search) {
      const collect = (n) => {
        if (nodeMatches(n, search) && !n.__synthetic) matches.add(n.id || nameOf(n));
        childrenOf(n).forEach(collect);
      };
      collect(root);
    }
    return { nodes: ns, edges: es, matchKey: matches };
  }, [tree, search]);

  return (
    <div className="p-4 overflow-auto max-h-[600px]">
      <div style={{ position: "relative", width: W, margin: "0 auto" }}>
        <svg width={W} height={H} style={{ display: "block" }}>
          {/* Edges first so bubbles draw on top */}
          {edges.map(([p, c], i) => {
            if (!p._pt || !c._pt) return null;
            return (
              <line
                key={`e${i}`}
                x1={p._pt.x} y1={p._pt.y}
                x2={c._pt.x} y2={c._pt.y}
                stroke="#1c1c1c"
                strokeWidth={0.8}
              />
            );
          })}

          {/* Bubbles */}
          {nodes.map((n, i) => {
            if (!n._pt) return null;
            const r       = n._pt.depth === 0 && n.__synthetic ? ROOT_R : bubbleRadius(n);
            const color   = colorFor(n.role);
            const isMatch = !search || matchKey.has(n.id || nameOf(n));
            const dim     = search && !isMatch && !n.__synthetic;

            return (
              <g
                key={n.id || `n${i}`}
                style={{ cursor: n.__synthetic ? "default" : "pointer", opacity: dim ? 0.18 : 1 }}
                onClick={() => !n.__synthetic && onSelectUser?.(n)}
                onMouseEnter={() => !n.__synthetic && setHover({ node: n })}
                onMouseLeave={() => setHover(null)}
              >
                <circle
                  cx={n._pt.x}
                  cy={n._pt.y}
                  r={r}
                  fill={`${color}33`}
                  stroke={`${color}cc`}
                  strokeWidth={1}
                />
                {/* Single-letter avatar inside bubble when the circle is
                    big enough to fit legibly. */}
                {r >= 12 && (
                  <text
                    x={n._pt.x} y={n._pt.y + 4}
                    textAnchor="middle"
                    fill="#fff"
                    fontSize={Math.min(r - 4, 13)}
                    fontWeight={700}
                    style={{ pointerEvents: "none" }}
                  >
                    {initialOf(n)}
                  </text>
                )}
              </g>
            );
          })}
        </svg>

        {hover && (
          <div
            style={{
              position: "absolute", top: 8, left: 8,
              background: "#0a0a0a", border: "1px solid #1c1c1c",
              borderRadius: 8, padding: "8px 12px",
              fontSize: 11, color: "#fff", pointerEvents: "none",
              minWidth: 180, maxWidth: 240,
            }}
          >
            <div style={{ fontWeight: 600 }}>{nameOf(hover.node)}</div>
            <div style={{ color: "#888", fontSize: 10, marginTop: 2 }}>
              {hover.node.title || hover.node.designation || "—"}
            </div>
            <div style={{ color: colorFor(hover.node.role), fontSize: 10, marginTop: 2 }}>
              {hover.node.role || "staff"}
              {hover.node.department ? ` · ${hover.node.department}` : ""}
            </div>
            {childrenOf(hover.node).length > 0 && (
              <div style={{ color: "#666", fontSize: 10, marginTop: 4 }}>
                {leafCount(hover.node)} people in subtree
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
